import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import moment from 'moment';
import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'react-i18next';
import * as ROUTES from '../../constants/routes';
import { blogService } from '../../services/blogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faThumbsUp, faThumbsDown, faReply } from '@fortawesome/free-solid-svg-icons';
import useFirebase from '../../hooks/useFirebase';
import '../../styles/blog.css';

export default function BlogPost() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const forceLtr = i18n.language === 'ar';
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToasts();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replies, setReplies] = useState({});
  const [editingReply, setEditingReply] = useState({});
  const [editingReplyText, setEditingReplyText] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const firebase = useFirebase();
  const user = firebase?.auth?.currentUser;

  const adRef = useRef(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const postData = await blogService.getPostById(id);
        setPost(postData);
      } catch (error) {
        addToast(t('blog.post.loadError'), {
          appearance: 'error',
          autoDismiss: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, addToast, t]);

  useEffect(() => {
    if (!post) return;
    setCommentsLoading(true);
    blogService.getComments(post.id)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setCommentsLoading(false));
  }, [post]);

  useEffect(() => {
    if (!post || !comments.length) return;
    const fetchReplies = async () => {
      const allReplies = {};
      for (const comment of comments) {
        allReplies[comment.id] = await blogService.getReplies(post.id, comment.id);
      }
      setReplies(allReplies);
    };
    fetchReplies();
  }, [post, comments]);

  useEffect(() => {
    if (!window.location.pathname.includes('/blog')) {
      return;
    }

    const scriptId = 'adsbygoogle-js';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2743550845530754';
      script.async = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }

    if (window.adsbygoogle && adRef.current) {
      try {
        window.adsbygoogle.push({});
      } catch (e) {}
    }
  }, []);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      addToast(t('blog.post.signInToComment'), {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const commentData = {
        author: user.displayName || user.email || 'Anonymous',
        content: newComment,
      };
      await blogService.addComment(post.id, commentData);
      const updatedComments = await blogService.getComments(post.id);
      setComments(updatedComments);
      setNewComment('');
      addToast(t('blog.post.commentSubmitted'), {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (err) {
      addToast(t('blog.post.commentSubmitError'), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (commentId) => {
    if (!user) return;
    await blogService.toggleLike(post.id, commentId, user.uid);
    const updatedComments = await blogService.getComments(post.id);
    setComments(updatedComments);
  };
  const handleDislike = async (commentId) => {
    if (!user) return;
    await blogService.toggleDislike(post.id, commentId, user.uid);
    const updatedComments = await blogService.getComments(post.id);
    setComments(updatedComments);
  };
  const handleReplySubmit = async (e, commentId) => {
    e.preventDefault();
    if (!user || !replyText.trim()) return;
    await blogService.addReply(post.id, commentId, {
      author: user.displayName || user.email || 'Anonymous',
      content: replyText,
    });
    setReplyText('');
    setReplyingTo(null);
    const updatedReplies = await blogService.getReplies(post.id, commentId);
    setReplies(prev => ({ ...prev, [commentId]: updatedReplies }));
    addToast(t('blog.post.replySubmitted'), { appearance: 'success', autoDismiss: true });
  };

  const handleEditReply = (commentId, reply) => {
    setEditingReply({ commentId, replyId: reply.id });
    setEditingReplyText(reply.content);
  };
  const handleSaveEditReply = async () => {
    const { commentId, replyId } = editingReply;
    await blogService.updateReply(post.id, commentId, replyId, editingReplyText);
    const updatedReplies = await blogService.getReplies(post.id, commentId);
    setReplies(prev => ({ ...prev, [commentId]: updatedReplies }));
    setEditingReply({});
    setEditingReplyText('');
    addToast(t('blog.post.replyUpdated'), { appearance: 'success', autoDismiss: true });
  };
  const handleDeleteReply = async (commentId, replyId) => {
    await blogService.deleteReply(post.id, commentId, replyId);
    const updatedReplies = await blogService.getReplies(post.id, commentId);
    setReplies(prev => ({ ...prev, [commentId]: updatedReplies }));
    addToast(t('blog.post.replyDeleted'), { appearance: 'success', autoDismiss: true });
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditingCommentText(comment.content);
  };
  const handleSaveEditComment = async () => {
    await blogService.updateComment(post.id, editingComment, editingCommentText);
    const updatedComments = await blogService.getComments(post.id);
    setComments(updatedComments);
    setEditingComment(null);
    setEditingCommentText('');
    addToast(t('blog.post.commentUpdated'), { appearance: 'success', autoDismiss: true });
  };
  const handleDeleteComment = async (commentId) => {
    await blogService.deleteComment(post.id, commentId);
    const updatedComments = await blogService.getComments(post.id);
    setComments(updatedComments);
    addToast(t('blog.post.commentDeleted'), { appearance: 'success', autoDismiss: true });
  };

  if (loading) {
    return (
      <div className="container text-center" dir={forceLtr ? 'ltr' : undefined}>
        <div className="spinner-border" role="status">
          <span className="sr-only">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container" dir={forceLtr ? 'ltr' : undefined}>
        <div className="alert alert-warning">
          {t('blog.post.notFound')}{' '}
          <Link to={ROUTES.BLOG}>{t('blog.post.backToAll')}</Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <article className="blog-post" dir={forceLtr ? 'ltr' : undefined}>
      <div className="container">
        {window.location.pathname.includes('/blog') && (
          <div className="my-3 text-center">
            <ins
              className="adsbygoogle"
              style={{ display: 'block', minHeight: 90 }}
              data-ad-client="ca-pub-2743550845530754"
              data-ad-slot="1234567890"
              data-ad-format="auto"
              data-full-width-responsive="true"
              ref={adRef}
            ></ins>
          </div>
        )}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              {post.createdAt ? moment(post.createdAt.toDate()).format('DD.MM.YYYY') : t('common.noDate')}
            </small>
            {post.category && (
              <span className="badge badge-primary">{post.category}</span>
            )}
          </div>
          <h2 className="blog-post-title" style={{ color: '#0A2342' }}>{post.title}</h2>
        </div>
        <div
          className="blog-post-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <div className="mt-4">
          <div className="mb-1 font-weight-bold">{t('blog.post.shareLabel')}</div>
          <div className="mb-3 d-flex gap-2">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm mr-2"
              title={t('blog.post.shareFacebook')}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#FFFFFF'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#F5F5D1'; }}
              style={{ color: '#F5F5D1' }}
            >
              <FontAwesomeIcon icon={faFacebook} />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm mr-2"
              title={t('blog.post.shareTwitter')}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#FFFFFF'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#F5F5D1'; }}
              style={{ color: '#F5F5D1' }}
            >
              <FontAwesomeIcon icon={faXTwitter} />
            </a>
          </div>
          <Link
            to={ROUTES.BLOG}
            className="btn btn-primary"
            onMouseEnter={(e) => { e.target.style.color = '#FFFFFF'; }}
            onMouseLeave={(e) => { e.target.style.color = '#F5F5D1'; }}
            style={{ color: '#F5F5D1' }}
          >
            {t('blog.post.backToBlog')}
          </Link>
        </div>

        <div className="mt-5">
          {commentsLoading ? (
            <div>{t('blog.post.loadingComments')}</div>
          ) : comments.length > 0 ? (
            <>
              <h3 className="mb-4" style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                {t('blog.post.commentsCount', { count: comments.length, title: post.title })}
              </h3>
              {comments.map((comment) => {
                const likeCount = comment.likes ? comment.likes.length : 0;
                const dislikeCount = comment.dislikes ? comment.dislikes.length : 0;
                const userLiked = user && comment.likes && comment.likes.includes(user.uid);
                const userDisliked = user && comment.dislikes && comment.dislikes.includes(user.uid);
                const isOwner = user && (comment.author === (user.displayName || user.email || 'Anonymous'));
                const isEditing = editingComment === comment.id;
                return (
                  <div key={comment.id} className="mb-4 p-3" style={{ background: '#f7f7f7', borderRadius: 8 }}>
                    <div style={{ fontWeight: 'bold' }}>{comment.author}</div>
                    {isEditing ? (
                      <>
                        <textarea
                          className="form-control mb-2"
                          rows={2}
                          value={editingCommentText}
                          onChange={e => setEditingCommentText(e.target.value)}
                        />
                        <button
                          className="btn btn-primary btn-sm mr-2"
                          onClick={handleSaveEditComment}
                          disabled={!editingCommentText.trim()}
                          onMouseEnter={(e) => { e.target.style.color = '#FFFFFF'; }}
                          onMouseLeave={(e) => { e.target.style.color = '#F5F5D1'; }}
                          style={{ color: '#F5F5D1' }}
                        >
                          {t('common.save')}
                        </button>
                        <button className="btn btn-link btn-sm" onClick={() => { setEditingComment(null); setEditingCommentText(''); }}>{t('common.cancel')}</button>
                      </>
                    ) : (
                      <>
                        <div className="mb-2">{comment.content}</div>
                        <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>{comment.createdAt && comment.createdAt.toDate ? comment.createdAt.toDate().toLocaleString() : ''}</div>
                        {isOwner && (
                          <>
                            <button className="btn btn-link btn-sm p-0 mr-2" onClick={() => handleEditComment(comment)}>{t('common.edit')}</button>
                            <button className="btn btn-link btn-sm text-danger p-0" onClick={() => handleDeleteComment(comment.id)}>{t('common.delete')}</button>
                          </>
                        )}
                      </>
                    )}
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <button
                        className={`btn btn-sm btn-outline-success mr-2${userLiked ? ' active' : ''}`}
                        disabled={!user}
                        title={user ? t('blog.post.likeAction') : t('blog.post.signInToLike')}
                        onClick={() => handleLike(comment.id)}
                      >
                        <FontAwesomeIcon icon={faThumbsUp} /> {t('blog.post.likeButton', { count: likeCount })}
                      </button>
                      <button
                        className={`btn btn-sm btn-outline-danger mr-2${userDisliked ? ' active' : ''}`}
                        disabled={!user}
                        title={user ? t('blog.post.dislikeAction') : t('blog.post.signInToDislike')}
                        onClick={() => handleDislike(comment.id)}
                      >
                        <FontAwesomeIcon icon={faThumbsDown} /> {t('blog.post.dislikeButton', { count: dislikeCount })}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={!user}
                        title={user ? t('blog.post.replyAction') : t('blog.post.signInToReply')}
                        onClick={() => setReplyingTo(comment.id)}
                      >
                        <FontAwesomeIcon icon={faReply} /> {t('blog.post.replyButton')}
                      </button>
                    </div>
                    {replies[comment.id] && replies[comment.id].length > 0 && (
                      <div className="ml-4 mt-2">
                        {replies[comment.id].map(reply => {
                          const isOwner = user && (reply.author === (user.displayName || user.email || 'Anonymous'));
                          const isEditingReplyActive = editingReply.replyId === reply.id && editingReply.commentId === comment.id;
                          return (
                            <div key={reply.id} className="mb-2 p-2" style={{ background: '#f0f0f0', borderRadius: 6 }}>
                              <div style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{reply.author}</div>
                              {isEditingReplyActive ? (
                                <>
                                  <textarea
                                    className="form-control mb-2"
                                    rows={2}
                                    value={editingReplyText}
                                    onChange={e => setEditingReplyText(e.target.value)}
                                  />
                                  <button
                                    className="btn btn-primary btn-sm mr-2"
                                    onClick={handleSaveEditReply}
                                    disabled={!editingReplyText.trim()}
                                    onMouseEnter={(e) => { e.target.style.color = '#FFFFFF'; }}
                                    onMouseLeave={(e) => { e.target.style.color = '#F5F5D1'; }}
                                    style={{ color: '#F5F5D1' }}
                                  >
                                    {t('common.save')}
                                  </button>
                                  <button className="btn btn-link btn-sm" onClick={() => { setEditingReply({}); setEditingReplyText(''); }}>{t('common.cancel')}</button>
                                </>
                              ) : (
                                <>
                                  <div className="mb-2">{reply.content}</div>
                                  <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>{reply.createdAt && reply.createdAt.toDate ? reply.createdAt.toDate().toLocaleString() : ''}</div>
                                  {isOwner && (
                                    <>
                                      <button className="btn btn-link btn-sm p-0 mr-2" onClick={() => handleEditReply(comment.id, reply)}>{t('common.edit')}</button>
                                      <button className="btn btn-link btn-sm text-danger p-0" onClick={() => handleDeleteReply(comment.id, reply.id)}>{t('common.delete')}</button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {replyingTo === comment.id && (
                      <form onSubmit={(e) => handleReplySubmit(e, comment.id)} className="ml-4 mt-3">
                        <div className="form-group">
                          <textarea
                            className="form-control"
                            rows={2}
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            placeholder={t('blog.post.replyPlaceholder')}
                          />
                        </div>
                        <button className="btn btn-primary btn-sm" disabled={!replyText.trim()}>{t('blog.post.replySubmit')}</button>
                        <button className="btn btn-link btn-sm ml-2" type="button" onClick={() => { setReplyText(''); setReplyingTo(null); }}>{t('common.cancel')}</button>
                      </form>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <div>{t('blog.post.noComments')}</div>
          )}
        </div>

        <div className="mt-5">
          <h3 style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>{t('blog.post.addComment')}</h3>
          <form onSubmit={handleCommentSubmit} className="mt-3">
            <div className="form-group">
              <textarea
                className="form-control"
                rows={5}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder={t('blog.post.commentPlaceholder')}
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={submitting || !newComment.trim()}>
              {submitting ? t('common.loading') : t('blog.post.submit')}
            </button>
          </form>
        </div>
      </div>
    </article>
  );
} 